import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Paper,
  Box,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  Tabs,
  Tab,
  styled
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';
import {
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  DesktopWindows,
  Smartphone,
  TabletAndroid,
  BugReport
} from '@mui/icons-material';
import {
  useGetDomainBreakdownByPartnerIdQuery,
  useGetPartnerSummaryByPartnerIdQuery,
  useGetUserAgentBreakdownByPartnerIdQuery
} from '../../../features/data_assets/dataAssetsApiSlice';
import FullMemberDomains from './FullMemberDomains';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA336A'];

const SmallBubble = styled('div')`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 12px;
  background-color: ${(props) => props.bgColor || '#8884d8'};
  color: white;
  margin-left: 8px;
  margin-bottom: 4px;
`;

const formatYMDAsLocal = (ymd) => {
  if (!ymd) return "—";
  if (typeof ymd !== "string") {
    // fallback if a Date object slips through
    const d = new Date(ymd);
    return isNaN(d) ? "—" : d.toLocaleDateString();
  }
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd; // already some other format
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); // local time construction
  return dt.toLocaleDateString();
};

const ADCTrafficAnalysis = ({ organization, isDiscreteMode, colors }) => {
  const [tabIndex, setTabIndex] = useState(0);

  // Define a fixed container height for both tabs
  const containerHeight = 400;

  // Domains queries
  const { data: adcResolutionProcessData, isLoading: isLoadingDomainData } =
    useGetDomainBreakdownByPartnerIdQuery(organization?.pid);
  const { data: partnerSummaryData } =
    useGetPartnerSummaryByPartnerIdQuery(organization?.pid);

  // User agent query
  const { data: userAgentData, isLoading: isLoadingUserAgentData } =
    useGetUserAgentBreakdownByPartnerIdQuery(organization?.pid);

  // Domains state
  const [memberData, setMemberData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDomains, setTotalDomains] = useState(0);
  const [openGuide, setOpenGuide] = useState(false);

  // Process domain data when available
  useEffect(() => {
    console.log("adcResolutionProcessData", adcResolutionProcessData);
    if (adcResolutionProcessData) {
      // Assume adcResolutionProcessData is an array; use the first element
      const memberDomainData = adcResolutionProcessData.records
        .map((record) => ({
          domain: record.domain,
          totalCount: record.totalCount
        }))
        .sort((a, b) => b.totalCount - a.totalCount);
      setMemberData(memberDomainData);
    }
  }, [adcResolutionProcessData]);

  useEffect(() => {
    if (partnerSummaryData) {
      setTotalPages(partnerSummaryData?.uniquePages || 0);
      setTotalDomains(partnerSummaryData?.uniqueDomains || 0);
    }
  }, [partnerSummaryData]);

  // Prepare pie chart data for domains tab
  const pieData = memberData.slice(0, 5).map((item, index) => ({
    name: isDiscreteMode ? `Domain ${index + 1}` : item.domain,
    value: item.totalCount,
    fill: COLORS[index % COLORS.length]
  }));
  const totalDomainVisits = memberData.reduce(
    (acc, item) => acc + item.totalCount,
    0
  );

  const handleOpenGuide = () => setOpenGuide(!openGuide);
  const arrowIcon = openGuide ? <ExpandLess /> : <ExpandMore />;

  // For the user agents tab, expect data in the following shape:
  // { recorded_date, partnerBreakdown: { partnerId, userAgents: [ { user_agent, totalCount } ] } }
  const userAgentRecords =
    userAgentData?.partnerBreakdown?.userAgents || [];

  // DataGrid columns for user agents
  const userAgentColumns = [
    {
      field: 'icon',
      headerName: '',
      width: 50,
      renderCell: (params) => getIconForUserAgent(params.row.user_agent),
      sortable: false,
      filterable: false
    },
    {
      field: 'user_agent',
      headerName: 'User Agent',
      flex: 1
    },
    {
      field: 'totalCount',
      headerName: 'Count',
      width: 100
    }
  ];

  // Function that returns an icon based on the user agent string.
  const getIconForUserAgent = (userAgent) => {
    if (!userAgent) return <InfoOutlined />;
    if (userAgent.includes('Desktop')) return <DesktopWindows />;
    if (userAgent.includes('Mobile')) return <Smartphone />;
    if (userAgent.includes('Tablet')) return <TabletAndroid />;
    if (userAgent.includes('Bot')) return <BugReport />;
    return <InfoOutlined />;
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: '20px',
          backgroundColor: colors.grey[700],
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)',
          padding: '20px',
          margin: '0 12px',
          overflow: 'hidden',
          position: 'relative',
          height: containerHeight
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ marginBottom: 2 }}
        >
          <Tab label="Page Domains" />
          <Tab label="User Agents" />
        </Tabs>

        {tabIndex === 0 && (
          // Domains tab content
          <Box sx={{ height: '100%' }}>
            <Grid container spacing={4} style={{ maxWidth: '100%' }}>
              <Grid
                item
                xs={12}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
  Top Domains for Member For{" "}
  {isLoadingDomainData
    ? "Loading..."
    : formatYMDAsLocal(adcResolutionProcessData?.recorded_date)}
</Typography>
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Box
                  width="100%"
                  height={200}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {isLoadingDomainData ? (
                    <CircularProgress />
                  ) : (
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      <ResponsiveContainer width="50%" height={200}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                            fill="#8884d8"
                            label
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <List dense={true} sx={{ padding: 0 }}>
                        {pieData.map((item, index) => (
                          <ListItem key={index} sx={{ padding: '2px 0' }}>
                            <ListItemText
                              primary={
                                isDiscreteMode
                                  ? `Domain ${index + 1}: ${item.value} occurrences`
                                  : `${item.name}: ${item.value} occurrences`
                              }
                              primaryTypographyProps={{
                                style: { color: colors.grey[100] }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
                <Typography variant="subtitle1">
                  Total Visits: {totalDomainVisits}
                </Typography>
              </Grid>
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 1
                }}
              >
                <SmallBubble bgColor="#00C49F">
                  Pages: {totalPages}
                </SmallBubble>
                <SmallBubble bgColor="#FF8042">
                  Domains: {totalDomains}
                </SmallBubble>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 60,
                  right: 10,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" sx={{ color: colors.grey[100], marginLeft: 1 }}>
                  All Domains
                </Typography>
                <IconButton
                  onClick={handleOpenGuide}
                  sx={{
                    color: colors.grey[100],
                    transition: 'transform 0.3s',
                    transform: openGuide ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  {arrowIcon}
                </IconButton>
              </Box>
              <Dialog
                open={openGuide}
                onClose={handleOpenGuide}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  style: {
                    borderRadius: '20px',
                    backgroundColor: colors.primary[700],
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)'
                  }
                }}
              >
                <FullMemberDomains memberData={memberData} />
              </Dialog>
            </Grid>
          </Box>
        )}

        {tabIndex === 1 && (
          // User Agents tab content
          <Box sx={{ height: '100%', width: '100%' }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              User Agent Analysis{' '}
              {userAgentData?.recorded_date
                ? `(Recorded on: ${new Date(userAgentData.recorded_date).toLocaleDateString()})`
                : '(Loading...)'}
            </Typography>
            {isLoadingUserAgentData ? (
              <CircularProgress />
            ) : (
              <DataGrid
                rows={userAgentRecords.map((item, index) => ({
                  id: index,
                  ...item
                }))}
                columns={[
                  {
                    field: 'icon',
                    headerName: '',
                    width: 50,
                    renderCell: (params) => getIconForUserAgent(params.row.user_agent),
                    sortable: false,
                    filterable: false
                  },
                  {
                    field: 'user_agent',
                    headerName: 'User Agent',
                    flex: 1
                  },
                  {
                    field: 'totalCount',
                    headerName: 'Count',
                    width: 100,
                    renderCell: (params) => params.value.toLocaleString()
                  }
                ]}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                autoHeight={false}
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none'
                  },
                  height: 250 // Adjust this value to keep the DataGrid within the fixed container
                }}
              />
            )}

          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default ADCTrafficAnalysis;
